import React, { useState, useCallback } from 'react';
import styles from './UserPillsDisplay.module.scss';
import UserEmailInput from './UserEmailInput';
import { truncateByCharacters } from '../../../utils/utils';
import Tooltip from '../../_custom/Tooltip/Tooltip';
import { crossIconBlack } from '../../../assets/hostedassets';

interface User {
    emailId: string;
    userId?: string;
    isExternalUser?: boolean;
    [key: string]: any;
}

interface UserPillsDisplayProps {
    selectedUsers: User[];
    selectableUsers: User[];
    onAddUser: (email: string, isExternal?: boolean) => Promise<void> | void;
    onRemoveUser: (email: string) => void;
    // Props from useComposeEmailLogic
    inputValue: string;
    showDropdown: boolean;
    filteredUsers: string[];
    selectedPills: Set<string>;
    isValidatingEmail: boolean;
    inputRef: React.RefObject<HTMLInputElement>;
    isSelectingFromDropdown: React.MutableRefObject<boolean>;
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    handleDropdownItemClick: (email: string) => void;
    handleInputBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
    handleInputFocus: () => void;
    handlePillClick: (email: string, e: React.MouseEvent) => void;
    handleCloseDropdown: () => void;
    setSelectedPills: React.Dispatch<React.SetStateAction<Set<string>>>;
}

const UserPillsDisplay: React.FC<UserPillsDisplayProps> = ({
    selectedUsers,
    selectableUsers,
    onAddUser,
    onRemoveUser,
    inputValue,
    showDropdown,
    filteredUsers,
    selectedPills,
    isValidatingEmail,
    inputRef,
    isSelectingFromDropdown,
    handleInputChange,
    handleKeyPress,
    handleDropdownItemClick,
    handleInputBlur,
    handleInputFocus,
    handlePillClick,
    handleCloseDropdown,
    setSelectedPills,
}) => {
    const [isExpanded, setIsExpanded] = useState(selectedUsers.length < 3);
    const maxVisibleUsers = 2; // Show only 2 users initially

    const renderCollapsedView = useCallback((): JSX.Element => {
        if (!selectedUsers?.length) return <span></span>;

        // Show first 2 users, then +N for the rest
        const visibleUsers = selectedUsers.slice(0, maxVisibleUsers);
        const hiddenCount = selectedUsers.length - maxVisibleUsers;

        // Create bullet point formatted content for tooltip
        const bulletPointEmails = selectedUsers
            .map((user) => user?.emailId || '')
            .filter(Boolean)
            .map((email) => `• ${email}`)
            .join('\n');

        return (
            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.25rem' }}>
                {visibleUsers.map((user, index) => {
                    return (
                        <div key={user.emailId || index} className={styles.CollapsedUserPill}>
                            <span className={styles.CollapsedEmail}>{truncateByCharacters(user.emailId || '', 25)}</span>
                            <img
                                className={styles.CollapsedRemoveButton}
                                src={crossIconBlack}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    // Only allow removal if there's more than 1 user
                                    if (selectedUsers.length > 1) {
                                        onRemoveUser(user.emailId);
                                    }
                                }}
                                alt="Remove"
                                aria-label={`Remove ${user.emailId}`}
                            />
                        </div>
                    );
                })}
                {hiddenCount > 0 && (
                    <Tooltip
                        content={
                            <div
                                style={{
                                    whiteSpace: 'pre-line',
                                    textAlign: 'left',
                                    minWidth: 'max-content',
                                }}
                            >
                                {bulletPointEmails}
                            </div>
                        }
                        direction="right"
                        fitContent={true}
                        hideDelay={500}
                    >
                        <span style={{ color: 'var(--primary-text-colour)', cursor: 'pointer' }} onClick={() => setIsExpanded(true)}>
                            +{hiddenCount} more
                        </span>
                    </Tooltip>
                )}
            </div>
        );
    }, [selectedUsers, maxVisibleUsers, onRemoveUser]);

    const renderExpandedView = useCallback((): JSX.Element => {
        return (
            <UserEmailInput
                selectedUsers={selectedUsers}
                selectableUsers={selectableUsers}
                onAddUser={onAddUser}
                onRemoveUser={onRemoveUser}
                inputValue={inputValue}
                showDropdown={showDropdown}
                filteredUsers={filteredUsers}
                selectedPills={selectedPills}
                isValidatingEmail={isValidatingEmail}
                inputRef={inputRef}
                isSelectingFromDropdown={isSelectingFromDropdown}
                handleInputChange={handleInputChange}
                handleKeyPress={handleKeyPress}
                handleDropdownItemClick={handleDropdownItemClick}
                handleInputBlur={handleInputBlur}
                handleInputFocus={handleInputFocus}
                handlePillClick={handlePillClick}
                handleCloseDropdown={handleCloseDropdown}
                setSelectedPills={setSelectedPills}
            />
        );
    }, [
        selectedUsers,
        selectableUsers,
        onAddUser,
        onRemoveUser,
        inputValue,
        showDropdown,
        filteredUsers,
        selectedPills,
        isValidatingEmail,
        inputRef,
        isSelectingFromDropdown,
        handleInputChange,
        handleKeyPress,
        handleDropdownItemClick,
        handleInputBlur,
        handleInputFocus,
        handlePillClick,
        handleCloseDropdown,
        setSelectedPills,
    ]);

    if (isExpanded) {
        return <div className={styles.UserPillsDisplay}>{renderExpandedView()}</div>;
    }

    return (
        <div className={styles.UserPillsDisplay}>
            <div className={styles.UsersList}>
                <span style={{ color: '#9D9D9D' }}>To:</span>
                {renderCollapsedView()}
            </div>
        </div>
    );
};

export default UserPillsDisplay;
